<!--
 © Copyright 2026 Daikin Industries, Ltd. All rights reserved.

 This software is the proprietary information of Daikin Industries, Ltd.
 By using this software, you agree to the following terms:

 1. You are granted a revocable non-exclusive, non-transferable license to use this software.
 2. You agree not to modify, distribute, or create derivative works based on this software.
 3. Unauthorized copying, distribution, or use of any portion of this software is strictly prohibited.
 4. This software is protected by copyright laws and international treaties.
 5. Any violation of these terms will result in the termination of your license and may result in legal action.
 6. This is Daikin's proprietary software and may not be used in an unauthorized manner or without express authorization from Daikin.
-->

<div class="home-container">
  <!-- Hero Section -->
  <section class="hero-section">
    <div class="hero-content">
      <div class="hero-logo-container">
        <img src="assets/images/api-mesh-logo.png" alt="API Mesh Logo" class="hero-logo">
      </div>
      <div class="hero-text-container">
        <div class="brand-header">
          <h1 class="brand-title">Daikin API Mesh (Athena)</h1>
          <p class="brand-subtitle">Empowering Digital Innovation Through Unified API Management</p>
        </div>
        <div class="hero-description">
          <p>Your gateway to Daikin's comprehensive API ecosystem, designed to accelerate integration,
             enhance collaboration, and drive business transformation.</p>
        </div>
      </div>
    </div>
  </section>

  <!-- Business Value Section -->
  <section class="value-section">
    <div class="section-header">
      <h2>Why Choose API Mesh?</h2>
      <p>Streamlined API discovery and management for modern enterprises</p>
    </div>

    <div class="value-grid">
      <div class="value-card">
        <div class="value-icon">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2L13.09 8.26L20 9L13.09 9.74L12 16L10.91 9.74L4 9L10.91 8.26L12 2Z" stroke="currentColor" stroke-width="2" stroke-linejoin="round"/>
          </svg>
        </div>
        <h3>Accelerated Development</h3>
        <p>Reduce integration time with standardized APIs, comprehensive documentation, and interactive testing tools.</p>
      </div>

      <div class="value-card">
        <div class="value-icon">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M10 13C10.4295 13.5741 10.9774 14.0491 11.6066 14.3929C12.2357 14.7367 12.9315 14.9411 13.6467 14.9923C14.3618 15.0435 15.0796 14.9403 15.7513 14.6897C16.4231 14.4392 17.0331 14.047 17.54 13.54L20.54 10.54C21.4508 9.59695 21.9548 8.33394 21.9434 7.02296C21.932 5.71198 21.4061 4.45791 20.4791 3.53087C19.5521 2.60383 18.298 2.07799 16.987 2.0666C15.676 2.0552 14.413 2.55918 13.47 3.47L11.75 5.18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M14 11C13.5705 10.4259 13.0226 9.95086 12.3934 9.60707C11.7643 9.26327 11.0685 9.05885 10.3533 9.00769C9.63819 8.95653 8.92037 9.05973 8.24864 9.31026C7.5769 9.56079 6.96687 9.95299 6.45996 10.46L3.45996 13.46C2.54917 14.403 2.04519 15.6661 2.05659 16.977C2.06798 18.288 2.59382 19.5421 3.52086 20.4691C4.4479 21.3962 5.70197 21.922 7.01295 21.9334C8.32393 21.9448 9.58694 21.4408 10.53 20.53L12.24 18.82" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <h3>Seamless Integration</h3>
        <p>Connect diverse systems and services through our unified API mesh architecture with intelligent routing.</p>
      </div>

      <div class="value-card">
        <div class="value-icon">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3 3V21H21" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M9 9L12 6L16 10L22 4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M22 4H18V8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <h3>Data-Driven Insights</h3>
        <p>Leverage rich data points and analytics to make informed business decisions and optimize operations.</p>
      </div>

      <div class="value-card">
        <div class="value-icon">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 22C12 22 20 18 20 12V5L12 2L4 5V12C4 18 12 22 12 22Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <h3>Enterprise Security</h3>
        <p>Built-in security policies, authentication, and encrypted communication ensure data protection.</p>
      </div>
    </div>
  </section>

  <!-- API Categories Section -->
  <section class="categories-section">
    <div class="section-header">
      <h2>Explore API Catalog</h2>
      <p>Discover APIs organized by domain expertise</p>
    </div>

    <div class="categories-grid">
      <div class="category-card" [routerLink]="['/apis']">
        <div class="category-header">
          <h3>Athena Core APIs</h3>
        </div>
        <p>Application registration, user management, and discoverability services for platform foundation.</p>
        <div class="category-footer">
          <span class="api-count">4 APIs Available</span>
          <span class="explore-btn">Explore →</span>
        </div>
      </div>

      <div class="category-card" [routerLink]="['/apis']">
        <div class="category-header">
          <h3>GCP Integration</h3>
        </div>
        <p>Cloud-native services for domain management and data point versioning with Google Cloud Platform.</p>
        <div class="category-footer">
          <span class="api-count">2 APIs Available</span>
          <span class="explore-btn">Explore →</span>
        </div>
      </div>

      <div class="category-card" [routerLink]="['/apis']">
        <div class="category-header">
          <h3>IoT & Smart Building</h3>
        </div>
        <p>Device management, domain control, and ontology services for connected building solutions.</p>
        <div class="category-footer">
          <span class="api-count">3 APIs Available</span>
          <span class="explore-btn">Explore →</span>
        </div>
      </div>
    </div>
  </section>

  <!-- Quick Actions Section -->
  <section class="actions-section">
    <div class="section-header">
      <h2>Get Started</h2>
      <p>Begin your integration journey</p>
    </div>

    <div class="actions-grid">
      <a class="action-card primary" [routerLink]="['/apis']">
        <div class="action-icon">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M2 6C2 5.46957 2.21071 4.96086 2.58579 4.58579C2.96086 4.21071 3.46957 4 4 4H20C20.5304 4 21.0391 4.21071 21.4142 4.58579C21.7893 4.96086 22 5.46957 22 6V18C22 18.5304 21.7893 19.0391 21.4142 19.4142C21.0391 19.7893 20.5304 20 20 20H4C3.46957 20 2.96086 19.7893 2.58579 19.4142C2.21071 19.0391 2 18.5304 2 18V6Z" stroke="currentColor" stroke-width="2"/>
            <path d="M8 2V22" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          </svg>
        </div>
        <div class="action-content">
          <h3>Browse API Catalog</h3>
          <p>Explore our comprehensive API documentation</p>
        </div>
      </a>

      <a class="action-card secondary" [routerLink]="['/api-mesh']">
        <div class="action-icon">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="3" stroke="currentColor" stroke-width="2"/>
            <circle cx="12" cy="5" r="2" stroke="currentColor" stroke-width="2"/>
            <circle cx="19" cy="12" r="2" stroke="currentColor" stroke-width="2"/>
            <circle cx="12" cy="19" r="2" stroke="currentColor" stroke-width="2"/>
            <circle cx="5" cy="12" r="2" stroke="currentColor" stroke-width="2"/>
            <path d="M12 9V7" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            <path d="M15 12H17" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            <path d="M12 15V17" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            <path d="M9 12H7" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          </svg>
        </div>
        <div class="action-content">
          <h3>API Mesh Overview</h3>
          <p>Discover service mesh capabilities and features</p>
        </div>
      </a>

      <a class="action-card tertiary" [routerLink]="['/api-mesh/explorer']">
        <div class="action-icon">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M21 16V8C20.9996 7.64928 20.9071 7.30481 20.7315 7.00116C20.556 6.69751 20.3037 6.44536 20 6.27L13 2.27C12.696 2.09446 12.3511 2.00205 12 2.00205C11.6489 2.00205 11.304 2.09446 11 2.27L4 6.27C3.69626 6.44536 3.44398 6.69751 3.26846 7.00116C3.09294 7.30481 3.00036 7.64928 3 8V16C3.00036 16.3507 3.09294 16.6952 3.26846 16.9988C3.44398 17.3025 3.69626 17.5546 4 17.73L11 21.73C11.304 21.9055 11.6489 21.9979 12 21.9979C12.3511 21.9979 12.696 21.9055 13 21.73L20 17.73C20.3037 17.5546 20.556 17.3025 20.7315 16.9988C20.9071 16.6952 20.9996 16.3507 21 16Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M3.27 6.96L12 12.01L20.73 6.96" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M12 22.08V12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <div class="action-content">
          <h3>Explore API Mesh</h3>
          <p>Full list of available APIs within the mesh</p>
        </div>
      </a>
    </div>
  </section>
</div>

<router-outlet></router-outlet>
